import { useState } from "react";
import { useSearch, useNavigate } from "@tanstack/react-router";
import { useAuth } from "@/hooks/useAuth";
import { dataApi } from "@/lib/api/admin-api";
import { useLingui } from "@lingui/react/macro";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { ApiDatatable } from "@/components/data-table";
import { getOrganizationColumns } from "./columns";
import { OrganizationTile } from "./OrganizationTile";
import {
  OrganizationFormDialog,
} from "./CreateOrganizationDialog";
import { ConfirmDeleteDialog } from "@/components/common/ConfirmDeleteDialog";
import type { Organization } from "@/types/organization";

export function OrganizationListing() {
  const { t, i18n } = useLingui();
  const { isAuthenticated } = useAuth();
  const searchParams = useSearch({ from: "/(protected)/_layout/organizations" });
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [editingOrganization, setEditingOrganization] = useState<Organization | null>(null);
  const [deletingOrganization, setDeletingOrganization] = useState<Organization | null>(null);

  const deleteMutation = useMutation({
    mutationFn: (id: number) => dataApi.deleteOrganization(id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["organizations"] });
      toast.success(t`Organization deleted`);
      setDeletingOrganization(null);
    },
    onError: () => toast.error(t`Failed to delete organization`),
  });

  const columns = getOrganizationColumns(i18n, setEditingOrganization, setDeletingOrganization);

  return (
    <>
      <ApiDatatable
        searchParams={searchParams}
        onNavigate={(updater) =>
          void navigate({ from: "/organizations", search: updater, replace: true })
        }
        queryKey={["organizations"]}
        queryFn={(params) =>
          dataApi.paginateOrganizations({
            search: params.search,
            page: params.page,
            limit: params.pageSize,
            sortBy: params.sortBy,
            sortOrder: params.sortOrder,
            isActive:
              params.status === "active"
                ? true
                : params.status === "inactive"
                  ? false
                  : undefined,
          }, { adminScope: true })
        }
        columns={columns}
        renderTile={(organization) => (
          <OrganizationTile
            organization={organization}
            onEdit={(org) => setEditingOrganization(org)}
          />
        )}
        getRowId={(organization) => String(organization.id)}
        onRowClick={(organization) => setEditingOrganization(organization)}
        enabled={isAuthenticated}
        searchPlaceholder={t`Search organizations…`}
        emptyMessage={t`No organizations found`}
        toolbarExtra={
          <Select
            value={searchParams.status}
            onValueChange={(status) =>
              void navigate({
                from: "/organizations",
                search: (prev) => ({
                  ...prev,
                  status: status as "all" | "active" | "inactive",
                  page: 1,
                }),
              })
            }
          >
            <SelectTrigger className="w-35">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t`All`}</SelectItem>
              <SelectItem value="active">{t`Active`}</SelectItem>
              <SelectItem value="inactive">{t`Inactive`}</SelectItem>
            </SelectContent>
          </Select>
        }
      />

      <OrganizationFormDialog
        organization={editingOrganization ?? undefined}
        open={!!editingOrganization}
        onOpenChange={(open) => {
          if (!open) setEditingOrganization(null);
        }}
      />

      <ConfirmDeleteDialog
        open={!!deletingOrganization}
        onOpenChange={(open) => {
          if (!open) setDeletingOrganization(null);
        }}
        title={t`Delete organization?`}
        description={t`You are about to permanently delete ${deletingOrganization?.name ?? ""}. This will cascade to all data belonging to this organization.`}
        bullets={[
          t`All concepts will be soft-deleted`,
          t`All regions and locations will be soft-deleted`,
          t`All users will be deactivated`,
          t`All recipes will be soft-deleted`,
          t`This action cannot be undone`,
        ]}
        confirmLabel={t`Delete organization`}
        onConfirm={() => {
          if (deletingOrganization) {
            deleteMutation.mutate(deletingOrganization.id);
          }
        }}
        isPending={deleteMutation.isPending}
      />
    </>
  );
}
